// The whole web page of a project's app (ADR 0021): its own sign-in page, its app bar and account menu,
// the project's apps (the app view), the full page of every pinned companion, and on every project page
// the companions' overlays (the chat window, the tours), the speed dial and the chart windows. A project's
// web/main.tsx renders <AppShell companions={…}/> with the `./app` entries of the SDKs it pins.
import { ACCOUNT_SECURITY_PAGE } from '@cortex/app-host/contract';
import { ConfirmProvider, Toaster } from '@cortex/ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as React from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
  useMatches,
  type RouteObject,
} from 'react-router';
import { applyBranding } from './branding.ts';
import { AppView } from './AppView.tsx';
import { AppAuthProvider, RequireSignIn, SignedOutOnly, useAppAuth } from './auth.tsx';
import { ChartWindows } from './ChartWindows.tsx';
import { CompanionDial, type DialHost } from './CompanionDial.tsx';
import { CompanionPagesProvider, useCompanionPages, type AppCompanion } from './companions.tsx';
import { pagePath, shownPath } from './paths.ts';
import { appInfo, appJson, moduleRuns } from './session.ts';
import { AppSignIn, signInSettled } from './SignIn.tsx';

/** The page's query cache: the SDK views share it (a chat tool that changed data marks it stale). */
export const appQueryClient = new QueryClient({
  defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false, staleTime: 0 } },
});

/** The home app's first page (/, its query kept). */
const Home = () => <Navigate to={{ pathname: '/', search: useLocation().search }} replace />;

function RouteError() {
  return (
    <div className="p-6 text-sm text-page-muted" data-testid="route-error">
      This page could not be shown.
    </div>
  );
}

function Layout({ companions }: { companions: AppCompanion[] }) {
  const info = appInfo();
  const slug = info.project.slug;
  const home = info.home ?? info.apps[0]?.key ?? null;
  // The app open on the page: the home app at its own addresses (its route is the path), another at /apps/<app>.
  const pathname = useLocation().pathname;
  const homeOpen = useMatches().some((m) => (m.handle as { home?: boolean } | undefined)?.home === true);
  const opened = /^\/apps\/([^/]+)\/?$/.exec(pathname)?.[1];
  const app = homeOpen ? home : opened ? decodeURIComponent(opened) : null;
  React.useEffect(signInSettled, []);
  const pages = useCompanionPages();
  // The app's own AI page is its admins' (the app's backend says whom it offers it to when it hands out the bearer):
  // asked of nobody else, so a member's page never draws a refused request, and a cortex-hosted instance (its AI is
  // seen in cortex) offers it to nobody.
  const { state } = useAppAuth();
  const admin = state.status === 'signed-in' && state.caller.aiPage === true;
  // The app's Access page (ADR 0066: who may use the app, each person's tenants) is its admins' too, and only when the
  // app mounts the access mini-app and the page's companion is here.
  const manager = state.status === 'signed-in' && state.caller.canManage === true;
  // The dial (the same component as cortex's Apps page) on this app's backend: the companions its
  // declaration names, narrowed to the SDKs it pins, and their pages in this shell.
  const host = React.useMemo<DialHost>(
    () => ({
      companions: async (_s, a) => {
        const declared = await appJson<{
          companions?: { key: string; label: string; page: string | null }[];
          chatUi?: string;
          dial?: string;
          declared?: boolean;
        }>(`/api/structure/apps/${encodeURIComponent(a)}/companions`);
        // The app draws its own chat (chat_ui 'app', the headless chat): the dial offers no Chat.
        const list = (declared?.companions ?? []).filter(
          (c) => moduleRuns(c.key) && !(c.key === 'chat' && declared?.chatUi === 'app'),
        );
        return {
          companions: withAdminPages(list, {
            aiPage: admin,
            access: manager && moduleRuns(ACCESS_PAGE) && !!pages[ACCESS_PAGE],
            // The project's governance and pipeline (their SDKs pinned because it has them): for the people who manage
            // the app, unless the app's declaration chose its own list.
            governance:
              manager && !declared?.declared && moduleRuns('governance') && !!pages[GOVERNANCE_PAGE],
            pipeline: manager && !declared?.declared && moduleRuns('pipeline') && !!pages[PIPELINE_PAGE],
          }),
          // The app's frame places the launcher (spec.dial 'app'): nothing floats over the app.
          dial: declared?.dial === 'app' ? ('app' as const) : ('host' as const),
        };
      },
      pages,
      pagePath: (page) => pagePath(page),
      // A link inside a companion's page opens in its window (the app stays underneath), with its back.
      windowNavigation: true,
    }),
    [pages, admin, manager],
  );
  const dial = React.useCallback(
    (p: { slug: string; app: string; hidden: boolean }) => (
      <CompanionDial host={host} slug={p.slug} app={p.app} hidden={p.hidden} />
    ),
    [host],
  );
  const dialOwned = companions.some((c) => c.ownsDial && c.Overlay);
  return (
    <>
      <div className="h-screen flex w-full overflow-hidden bg-page-bg" data-testid="standalone-shell">
        {/* No bar: the app (its frame) or an SDK page, full-bleed, as in cortex's preview. */}
        <main className="flex-1 flex flex-col overflow-hidden min-w-0">
          <div className="flex-1 overflow-auto" data-testid="page" data-tour="page">
            {/* The home app is this layout's own: '/' and every route of it share one frame, never remounted. */}
            {homeOpen ? <AppView /> : <Outlet />}
          </div>
        </main>
      </div>
      {companions.map((c) =>
        c.Overlay ? <c.Overlay key={c.module} slug={slug} app={app} dial={dial} /> : null,
      )}
      {app && !dialOwned && <CompanionDial host={host} slug={slug} app={app} />}
      <ChartWindows />
    </>
  );
}

/** The app's own AI page (its admins'), a page of the shell beside the companions'. */
const AI_PAGE = 'ai-use';
/** The access mini-app's page (its companion's path and the module it belongs to). */
const ACCESS_PAGE = 'access';
/** The governance SDK's Data Dictionary and the pipeline SDK's page (their companions' pages). */
const GOVERNANCE_PAGE = 'data-dictionary';
const PIPELINE_PAGE = 'pipeline';

/**
 * The dial's entries: the app's declared companions, then the pages that are its admins' only: the project's Pipeline
 * and Governance (when it has them and the app's declaration did not choose its own list; never twice), Access (when
 * the app mounts the access mini-app and its page is here) and the app's own AI use.
 */
export function withAdminPages<T extends { key: string; label: string; page: string | null }>(
  list: T[],
  o: { aiPage: boolean; access: boolean; governance?: boolean; pipeline?: boolean },
): (T | { key: string; label: string; page: string })[] {
  const has = (k: string) => list.some((c) => c.key === k);
  return [
    ...list,
    ...(o.pipeline && !has('pipeline') ? [{ key: 'pipeline', label: 'Pipeline', page: PIPELINE_PAGE }] : []),
    ...(o.governance && !has('governance')
      ? [{ key: 'governance', label: 'Governance', page: GOVERNANCE_PAGE }]
      : []),
    ...(o.access ? [{ key: ACCESS_PAGE, label: 'Access', page: ACCESS_PAGE }] : []),
    ...(o.aiPage ? [{ key: 'ai', label: 'AI use', page: AI_PAGE }] : []),
  ];
}

export function appRoutes(companions: AppCompanion[]): RouteObject[] {
  const pinned = companions.filter((c) => moduleRuns(c.module));
  const pages = Object.assign(
    { [AI_PAGE]: () => import('./AiUsePage.tsx').then((m) => m.AiUsePage) },
    ...pinned.map((c) => c.pages ?? {}),
  ) as Record<string, () => Promise<React.ComponentType>>;
  return [
    {
      element: <SignedOutOnly />,
      children: [{ path: '/login', element: <AppSignIn /> }],
    },
    {
      path: '/',
      element: <RequireSignIn />,
      errorElement: <RouteError />,
      children: [
        {
          path: ACCOUNT_SECURITY_PAGE.slice(1),
          lazy: async () => ({ Component: (await import('./SecurityPage.tsx')).SecurityPage }),
          hydrateFallbackElement: <div className="min-h-screen bg-page-bg" />,
          errorElement: <RouteError />,
        },
        {
          element: (
            <CompanionPagesProvider pages={pages}>
              <Layout companions={pinned} />
            </CompanionPagesProvider>
          ),
          children: [
            // The home app's first page and every address the shell does not name (its routes): the layout holds
            // its frame, so it stays as the route moves.
            { index: true, handle: { home: true }, errorElement: <RouteError /> },
            { path: 'apps', element: <Home /> },
            { path: 'apps/:viewName', element: <AppView />, errorElement: <RouteError /> },
            ...Object.entries(pages).map(([path, load]): RouteObject => ({
              path,
              lazy: async () => ({ Component: await load() }),
              hydrateFallbackElement: <div className="min-h-full bg-page-bg" />,
              errorElement: <RouteError />,
            })),
            { path: '*', handle: { home: true }, errorElement: <RouteError /> },
          ],
        },
      ],
    },
  ];
}

/** The page's router on the browser's address: the home app's /apps/<home> and /apps are shown at / first. */
export function createAppRouter(routes: RouteObject[]) {
  const i = appInfo();
  const at = window.location;
  const want = shownPath(at.pathname, { home: i.home ?? i.apps[0]?.key ?? null });
  if (want !== at.pathname)
    window.history.replaceState(window.history.state, '', `${want}${at.search}${at.hash}`);
  return createBrowserRouter(routes);
}

export function AppShell({ companions }: { companions: AppCompanion[] }) {
  const router = React.useMemo(() => createAppRouter(appRoutes(companions)), [companions]);
  return (
    <QueryClientProvider client={appQueryClient}>
      <AppAuthProvider>
        <ConfirmProvider>
          <RouterProvider router={router} />
          <Toaster />
        </ConfirmProvider>
      </AppAuthProvider>
    </QueryClientProvider>
  );
}

/** Renders the app's page into #root, in the app's own colour from the first paint. */
export async function mountApp(companions: AppCompanion[]) {
  applyBranding();
  const { createRoot } = await import('react-dom/client');
  createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <AppShell companions={companions} />
    </React.StrictMode>,
  );
}
